import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { Send, Mail } from "lucide-react";
import { toast } from "sonner";

export const Footer = () => {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email, lang });
      toast.success(t("newsletter_ok"));
      setEmail("");
    } catch { toast.error("Error"); }
  };

  return (
    <footer className="border-t border-[#272A34] bg-[#0B0C0E] mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center mb-4">
              <img src="/brand/swiss-pagan-logo-white.png" alt="swiss-pagan.ch" className="h-14 w-auto object-contain" />
            </div>
            <p className="text-sm text-stone-400 leading-relaxed">{t("footer_about")}</p>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-4">{t("footer_explore")}</h4>
            <ul className="space-y-2.5 text-sm text-stone-400">
              <li><Link to="/events" className="hover:text-amber-400 transition-colors">{t("nav_events")}</Link></li>
              <li><Link to="/artistes" className="hover:text-amber-400 transition-colors" data-testid="footer-artists">{t("nav_artists")}</Link></li>
              <li><Link to="/festivals" className="hover:text-amber-400 transition-colors" data-testid="footer-festivals">{t("nav_festivals")}</Link></li>
              <li><Link to="/organisateurs" className="hover:text-amber-400 transition-colors" data-testid="footer-organizers">{t("nav_organizers")}</Link></li>
              <li><Link to="/map" className="hover:text-amber-400 transition-colors">{t("nav_map")}</Link></li>
              <li><Link to="/directory" className="hover:text-amber-400 transition-colors">{t("nav_directory")}</Link></li>
              <li><Link to="/campfire" className="hover:text-amber-400 transition-colors">Campfire</Link></li>
              <li><Link to="/community" className="hover:text-amber-400 transition-colors">{t("nav_community")}</Link></li>
              <li><Link to="/cantons" className="hover:text-amber-400 transition-colors" data-testid="footer-cantons">{t("nav_cantons")}</Link></li>
              <li><Link to="/store" className="hover:text-amber-400 transition-colors">{t("nav_store")}</Link></li>
              <li><Link to="/membership" className="hover:text-amber-400 transition-colors">{t("nav_membership")}</Link></li>
              <li><Link to="/support" className="hover:text-amber-400 transition-colors" data-testid="footer-support">{t("nav_support")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-4">{t("footer_legal")}</h4>
            <ul className="space-y-2.5 text-sm text-stone-400">
              <li><Link to="/submit" className="hover:text-amber-400 transition-colors" data-testid="footer-pro-publish">{t("nav_announce")}</Link></li>
              <li><Link to="/professionnels" className="hover:text-amber-400 transition-colors" data-testid="footer-professionals">{t("nav_professionals")}</Link></li>
              <li><Link to="/opportunites" className="hover:text-amber-400 transition-colors" data-testid="footer-opportunities">{t("nav_opportunities")}</Link></li>
              <li><Link to="/pro/fiche" className="hover:text-amber-400 transition-colors" data-testid="footer-pro-create">Créer ma fiche</Link></li>
              <li><Link to="/organisateurs" className="hover:text-amber-400 transition-colors">{t("nav_organizers")}</Link></li>
              <li><Link to="/devenir-partenaire" className="hover:text-amber-400 transition-colors" data-testid="footer-become-partner">{t("nav_become_partner")}</Link></li>
              <li><Link to="/partenaires" className="hover:text-amber-400 transition-colors" data-testid="footer-partners">{t("nav_partners")}</Link></li>
            </ul>
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-4 mt-8">{t("footer_legal")}</h4>
            <ul className="space-y-2.5 text-sm text-stone-400">
              <li><Link to="/legal/privacy" className="hover:text-amber-400 transition-colors">{t("footer_privacy")}</Link></li>
              <li><Link to="/legal/terms" className="hover:text-amber-400 transition-colors">{t("footer_terms")}</Link></li>
              <li><Link to="/legal/notice" className="hover:text-amber-400 transition-colors">{t("footer_legal_notice")}</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-amber-400 transition-colors">{t("footer_cookies")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-4">{t("newsletter_title")}</h4>
            <p className="text-sm text-stone-400 mb-4">{t("newsletter_desc")}</p>
            <form onSubmit={subscribe} className="flex gap-2">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletter_ph")}
                className="flex-1 bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              />
              <button data-testid="newsletter-submit" type="submit" className="bg-amber-600 hover:bg-amber-500 text-stone-950 px-3 rounded-md transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-6">
              <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">{t("footer_contact")}</h4>
              <a href="mailto:contact@swiss-pagan.ch" data-testid="footer-contact-email" className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-amber-400 transition-colors">
                <Mail className="w-4 h-4 text-amber-600/80" /> contact@swiss-pagan.ch
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-[#272A34] flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-500 font-mono">
          <span>© {new Date().getFullYear()} swiss-pagan.ch — {t("footer_rights")}</span>
          <span className="tracking-[0.2em] uppercase">{t("brand_tag")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
